import React, { useEffect, useState } from "react";
import { Button } from "../design/button";
import { PlusIcon } from "@heroicons/react/20/solid";
import { Professional } from "../models/Professional";
import { usePlaidLink } from "react-plaid-link";
import { PlaidService } from "../services/PlaidService";

interface Props {
  professional?: Professional;
  className?: string;
  children?: React.ReactNode;
}

interface Errors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  bio?: string;
  roles?: string;
}

const PlaidButton: React.FC<Props> = ({
  professional,
  className,
  children,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [linkToken, setLinkToken] = useState<string | null>(null);

  // Setup Plaid Link hook
  const { open, ready } = usePlaidLink({
    token: linkToken || "", // safe fallback to avoid undefined
    onSuccess: async (public_token, metadata) => {
      await PlaidService.exchangePublicToken(public_token);
      await PlaidService.syncAccounts();
      await PlaidService.syncTransactions();
    },
    onExit: (err, metadata) => {
      console.warn("Plaid exit", err, metadata);
    },
  });

  // When you want to open Plaid
  const handleCreateLinkToken = async () => {
    setIsLoading(true);
    try {
      const token = await PlaidService.createLinkToken();
      setLinkToken(token); // Triggers usePlaidLink to reinit
    } catch (error) {
      console.error("Error creating link token", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Automatically open when token and hook are ready
  useEffect(() => {
    if (linkToken && ready) {
      open();
    }
  }, [linkToken, ready]);

  return (
    <Button
      type="button"
      className={className}
      onClick={handleCreateLinkToken}
      disabled={isLoading}
    >
      {children ?? (
        <div className="flex items-center gap-2">
          <PlusIcon className="w-4 h-4" />
          <span className="hidden sm:block">
            {professional ? "Edit Professional" : "Add Professional"}
          </span>
        </div>
      )}
    </Button>
  );
};

export default PlaidButton;
